import * as msal from "@azure/msal-browser";
import { msalConfig, msalConfigured } from "./authConfig.js";

/**
 * Why MSAL is not constructed on this page, or `null` when it is.
 *
 * - No `window.huMsalConfig` from the server (see authConfig.js): MSAL would
 *   throw on an undefined authority on every page load and every click on
 *   "Accedi".
 * - No Web Crypto API: MSAL 5 checks `window.crypto` and `crypto.subtle` in the
 *   PublicClientApplication constructor and throws `crypto_nonexistent`
 *   synchronously. This module runs that constructor on import, so the throw
 *   aborted the whole of index.js, every feature after it included, and reached
 *   `onerror`: Sentry HUOPENAIR-7V, ~7,000 events a day since 2026-10-12, all
 *   from iPhone WKWebViews on the home pages. The `catch` around `initialize()`
 *   below (HUOPENAIR-2S / 2Y) never saw it, since it throws before that.
 *
 * Either way the login UI stays inert and the page reports "logged out".
 */
const unavailableReason = !msalConfigured
    ? "services.azure_b2c is not configured on this server"
    : !window.crypto?.subtle
        ? "this browser exposes no Web Crypto API"
        : null;

export const msalInstance = unavailableReason
    ? null
    : new msal.PublicClientApplication(msalConfig);

const loginRequest = {
    scopes: ["openid", "profile"],
};

let onStateChange = null;

function notifyStateChange() {
    if (!onStateChange) return;

    if (!msalInstance) {
        onStateChange({ isAuthenticated: false, account: null, inProgress: false });
        return;
    }

    const accounts = msalInstance.getAllAccounts();
    onStateChange({
        isAuthenticated: accounts.length > 0,
        account: msalInstance.getActiveAccount(),
        inProgress: false,
    });
}

export function onAuthStateChange(callback) {
    onStateChange = callback;
}

let initialization = null;

/**
 * Idempotent. This module starts it on import (`msalReady`) and index.js calls
 * it again once its state listener is registered; before, that ran initialize(),
 * handleRedirectPromise() and ssoSilent() twice on every page.
 */
export function initializeMsal() {
    if (!initialization) {
        initialization = doInitialize();
    }

    return initialization;
}

async function doInitialize() {
    if (!msalInstance) {
        // Let the importing module register its listener first: this runs
        // synchronously up to the first await, i.e. while auth.js is being imported.
        await Promise.resolve();
        console.warn(`[MyHU] login disabled: ${unavailableReason}.`);
        notifyStateChange();
        return;
    }

    try {
        await msalInstance.initialize();
    } catch (err) {
        // `initialize()` is where MSAL first needs the Web Crypto API: it
        // pre-generates the PKCE codes there. A browser that exposes no
        // `crypto.subtle` — an insecure origin (an in-app browser or a proxy
        // that drops the page to http), a hardened privacy mode, an old
        // WebView — rejects it with `crypto_nonexistent`, and nothing was
        // catching that promise, so it reached `onunhandledrejection` on every
        // page load: Sentry HUOPENAIR-2Y / HUOPENAIR-2S. Login genuinely
        // cannot work on such a browser, so treat it like an unconfigured
        // server: report "logged out" once and leave the login UI inert.
        // `unavailableReason` now stops a browser with no `crypto.subtle`
        // before the constructor; this stays for whatever initialize() can
        // still reject with on a browser that passed that check.
        console.warn("[MyHU] login unavailable:", err?.errorCode || err?.message || err);
        notifyStateChange();
        return;
    }

    try {
        const response = await msalInstance.handleRedirectPromise();
        if (response) {
            handleResponse(response);
        }
    } catch (err) {
        console.error("Redirect error:", err);
        clearMsalHash();
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        // Known from the cache (localStorage, see authConfig.js) or from the redirect
        // that just completed: report it now, no network needed. GA4's page_view is
        // meant to wait for this `login_status`, so every round trip here is a delay
        // on the first hit of every page.
        if (!msalInstance.getActiveAccount()) {
            msalInstance.setActiveAccount(accounts[0]);
        }
        notifyStateChange();
    } else {
        // Not authenticated here — ask B2C for an existing session silently (iframe
        // only, no redirect). blank.html as redirectUri keeps the iframe from loading
        // the full app; it must be registered as a SPA redirect URI on Azure B2C.
        await trySsoSilent(); // notifies in its finally, whatever the outcome
    }

    // Retry ssoSilent when the user returns to this tab (e.g. after logging in on another app)
    window.addEventListener("focus", () => {
        if (msalInstance.getAllAccounts().length === 0) {
            trySsoSilent();
        }
    });
}

async function trySsoSilent() {
    try {
        const ssoResult = await msalInstance.ssoSilent({
            ...loginRequest,
            redirectUri: window.location.origin + "/blank.html",
            prompt: "None"
        });
        if (ssoResult?.account) {
            msalInstance.setActiveAccount(ssoResult.account);
        }
    } catch (error) {
        console.warn("[SSO] ssoSilent failed:", error.errorCode, error.message);
    } finally {
        notifyStateChange(); // always fires, with the correct final state
    }
}

function clearMsalHash() {
    const hash = window.location.hash;

    if (
        !hash.includes("code=") &&
        !hash.includes("error=") &&
        !hash.includes("error_description=")
    ) {
        return;
    }

    const url = new URL(window.location.href);
    url.hash = "";
    window.history.replaceState({}, document.title, url.toString());
}

/**
 * The two below are wired straight to a button's click handler, so a rejection
 * has no caller to catch it. MSAL rejects `loginRedirect` with
 * `interaction_in_progress` whenever another interaction is still open — a
 * double click, a redirect that never came back and left the flag in
 * sessionStorage, a second tab — which is how Sentry HUOPENAIR-2X was raised.
 * Neither is worth an unhandled rejection: the click simply does nothing.
 */
export async function loginRedirect() {
    if (!msalInstance) {
        console.warn(`[MyHU] login disabled: ${unavailableReason}.`);
        return;
    }

    try {
        await msalInstance.loginRedirect(loginRequest);
    } catch (err) {
        console.warn("[MyHU] login failed:", err?.errorCode || err?.message || err);
    }
}

export async function logout() {
    if (!msalInstance) return;

    try {
        const account = msalInstance.getActiveAccount();
        await msalInstance.logoutRedirect({ account });
    } catch (err) {
        console.warn("[MyHU] logout failed:", err?.errorCode || err?.message || err);
    }
}

function handleResponse(response) {
    msalInstance.setActiveAccount(response.account);
}

export const msalReady = initializeMsal();
